import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import Logo from "../components/Logo";
import {
  Lock,
  Store,
  Cpu,
  Mail,
  ArrowRight,
  CheckCircle2
} from "lucide-react";

// ============ ADMIN ACCESS CONTROL ============
const DEMO_ADMIN_EMAILS = [
  "reynir@replydriven.com",
  "reyniroskarsson2310@gmail.com"
];
const DEMO_SECRET_KEY = "rd_demo_2026";

// ============ ONBOARDING STEPS ============
const ONBOARDING_STEPS = [
  {
    number: 1,
    title: "Connect your store",
    description: "One-click Shopify integration with read-only access",
    icon: Store,
    iconBg: "bg-[#D4F34A]",
    iconColor: "text-black"
  },
  {
    number: 2,
    title: "We analyze your data",
    description: "AI reviews orders, products, and customer patterns",
    icon: Cpu,
    iconBg: "bg-[#D4F34A]",
    iconColor: "text-black"
  },
  {
    number: 3,
    title: "Get your weekly insight",
    description: "One actionable recommendation delivered every Monday",
    icon: Mail,
    iconBg: "bg-[#D4F34A]",
    iconColor: "text-black"
  }
];

const DemoOnboardingFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const secretKey = searchParams.get("key");
    if (
      (user?.email && DEMO_ADMIN_EMAILS.includes(user.email.toLowerCase())) ||
      secretKey === DEMO_SECRET_KEY
    ) {
      setAuthorized(true);
    } else if (user !== undefined) {
      const timer = setTimeout(() => {
        if (!authorized) navigate("/");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, searchParams, navigate, authorized]);

  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-[#262626] bg-[#050505]">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between">
          <Logo showText />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-8 py-16 flex items-center justify-center min-h-[calc(100vh-120px)]">
        <div className="w-full">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
              How it works
            </h1>
            <p className="text-text-muted text-lg">
              Get started in under 2 minutes
            </p>
          </div>

          {/* Steps */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-4">
            {ONBOARDING_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === ONBOARDING_STEPS.length - 1;
              
              return (
                <div key={step.number} className="flex items-center">
                  {/* Step Card */}
                  <div className="flex flex-col items-center text-center w-64">
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-2xl ${step.iconBg} flex items-center justify-center mb-4`}>
                      <Icon className={`w-8 h-8 ${step.iconColor}`} />
                    </div>
                    
                    {/* Step Number */}
                    <div className="text-[#D4F34A] text-sm font-medium mb-2">
                      Step {step.number}
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-semibold text-lg text-white mb-2">
                      {step.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-text-muted text-sm">
                      {step.description}
                    </p>
                  </div>
                  
                  {/* Arrow (not on last item) */}
                  {!isLast && (
                    <div className="hidden md:block mx-4">
                      <ArrowRight className="w-6 h-6 text-[#262626]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom Features */}
          <div className="mt-16 flex flex-wrap justify-center gap-6 text-sm text-text-muted">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#D4F34A]" />
              <span>Read-only access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#D4F34A]" />
              <span>No code required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#D4F34A]" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </main>

      {/* Demo Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#050505] border-t border-[#262626] py-2 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-xs text-text-muted">
          <span>Internal Demo • Not for public distribution</span>
          <span>Onboarding Flow</span>
        </div>
      </footer>
    </div>
  );
};

export default DemoOnboardingFlow;
